import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks/hooks";
import FixedProductCard from "./PaymentCard";
import { PlanListsProps } from "./types";
import { convertCurrencyAmount, formatCurrency } from "../../utils/utils";

const PlanLists = (props: PlanListsProps) => {
  const { data: appData } = useAppSelector((state) => state.appData);
  const {
    selectedCurrency,
    currency: { items: localeCurrencies },
  } = useAppSelector((state) => state.locale);
  const { user } = useAppSelector((state) => state.auth);
  // Extract the last path segment as the category key
  const location = useLocation();
  const path = location.pathname;
  const segments = path.split("/").filter(Boolean); // ["products", "data", "MTN"]
  const formattedProduct = props.productType;
  const formattedProvider =
    segments.length >= 1 ? segments[segments.length - 1] : ""; //MTN

  const productSection = appData[formattedProduct];
  const providers = Array.isArray(productSection?.providers)
    ? productSection.providers
    : [];
  const categories = Array.isArray(productSection?.categories)
    ? productSection.categories
    : [];
  const productPlans = Array.isArray(productSection?.plans)
    ? productSection.plans
    : [];

  const provider = providers.find(
    (item: { slug: string }) => item.slug === formattedProvider,
  );

  const payButtonRef = useRef<HTMLButtonElement>(null);
  const filteredCategories = categories.filter(
    (category: any) => category.provider_id === provider?.id,
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [validationError, setValidationError] = useState<{
    field: "unique_number" | "additional" | "none";
    message: string | null;
  }>({ field: "none", message: null });
  const [verifyMessage, setVerifyMessage] = useState("");
  const [isVerifyingRecipient, setIsVerifyingRecipient] = useState(false);

  // Add state for inputs
  const [uniqueNumber, setUniqueNumber] = useState("");
  const [additionalInputValue, setAdditionalInputValue] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    if (filteredCategories.length > 0) {
      setSelectedCategoryId(filteredCategories[0].id);
      setSelectedPlan(null);
      return;
    }
    setSelectedCategoryId(0);
    setSelectedPlan(null);
  }, [provider?.id, filteredCategories.length]);
  // Filter plans based on the selected category
  const plans = productPlans.filter(
    (plan: any) => plan.category_id === selectedCategoryId,
  );

  const handlePlanClick = (plan: any) => {
    setSelectedPlan(plan);

    setTimeout(() => {
      if (payButtonRef.current) {
        payButtonRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 200);
  };

  const currencies = localeCurrencies || appData.currencies || [];
  const getCurrencyById = (currencyId?: number) =>
    currencies.find((currency: any) => currency.id === currencyId);
  const dgCoinCurrency = currencies.find(
    (currency: any) => currency.symbol === "Coins",
  );

  const formatConvertedPrice = (
    amount: number | string,
    currencyId?: number,
  ) => {
    const fromCurrency =
      getCurrencyById(currencyId) || selectedCurrency || currencies[0];
    const toCurrency = selectedCurrency || fromCurrency;
    const numericAmount = Number(amount);
    const converted = convertCurrencyAmount(
      numericAmount,
      fromCurrency,
      toCurrency,
    );
    return {
      symbol: toCurrency?.symbol || "",
      value: formatCurrency(converted),
    };
  };

  const formatDGCoinPrice = (amount: number | string, currencyId?: number) => {
    const fromCurrency = getCurrencyById(currencyId) || dgCoinCurrency;
    if (!fromCurrency || !dgCoinCurrency) {
      return formatCurrency(Number(amount));
    }
    const numericAmount = Number(amount);
    const converted = convertCurrencyAmount(
      numericAmount,
      fromCurrency,
      dgCoinCurrency,
    );
    return formatCurrency(converted);
  };

  const recipientLabel = provider?.recipient_label || "Recipient";
  const additionalInputEnabled = Boolean(provider?.additional_input);
  const additionalInputLabel =
    provider?.additional_input_label || "Additional Details";
  const additionalInputType = provider?.additional_input_type || "text";
  const additionalInputPlaceholder =
    provider?.additional_input_placeholder || "";
  const additionalInputName =
    provider?.additional_input_form_name || "additional_input";
  const shouldVerifyRecipient = Boolean(provider?.verify_recipient);

  const purchaseEndpointMap: Record<string, string> = {
    airtime: "airtime-purchase",
    data: "data-purchase",
    electric: "electric-purchase",
    cable: "cable-purchase",
    gift_cards: "gift-cards-purchase",
    game_cards: "game-cards-purchase",
    topup: "top-ups-purchase",
    dg_coins: "dgcoin-purchase",
  };

  const verifyEndpoint = selectedPlan?.id
    ? `${apiUrl}/${purchaseEndpointMap[props.productType]}/verify/${selectedPlan.id}/${encodeURIComponent(uniqueNumber.trim())}`
    : "";

  const extractVerifyMessage = (payload: any) =>
    payload?.message ||
    payload?.data?.message ||
    payload?.error ||
    "Unable to verify recipient.";

  const handleAdditionalInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const nextValue = event.target.value;
    if (validationError.field === "additional") {
      setValidationError({ field: "none", message: null });
    }
    if (additionalInputType === "number") {
      setAdditionalInputValue(nextValue.replace(/[^0-9]/g, ""));
      return;
    }
    setAdditionalInputValue(nextValue);
  };

  const handleVerifyRecipient = async () => {
    if (!shouldVerifyRecipient) return;

    setVerifyMessage("");

    const trimmedUniqueNumber = uniqueNumber.trim();
    if (!trimmedUniqueNumber) {
      setValidationError({
        field: "unique_number",
        message: `${recipientLabel} is required.`,
      });
      setVerifyMessage("");
      return;
    }

    if (!selectedPlan?.id) {
      setVerifyMessage("Select a plan before verifying recipient.");
      return;
    }

    setValidationError({ field: "none", message: null });
    setIsVerifyingRecipient(true);

    try {
      const response = await axios.get(verifyEndpoint);
      setVerifyMessage(extractVerifyMessage(response.data));
    } catch (error: any) {
      setVerifyMessage(
        extractVerifyMessage(error?.response?.data) ||
          error?.message ||
          "Unable to verify recipient.",
      );
    } finally {
      setIsVerifyingRecipient(false);
    }
  };

  useEffect(() => {
    setVerifyMessage("");
  }, [provider?.id, selectedPlan?.id]);

  return (
    <div className=" rounded-lg border border-border-border1 dark:border-tabs w-full lg:w-[660px]">
      {/* Cards */}
      <div className="w-full dark:bg-[#FFFFFF1A] border-b border-border-1 dark:border-tabs">
        <p className="font-semibold dark:text-white p-4  text-sm lg:text-base ">
          Products
        </p>
      </div>

      {/* Categories Filter if more than 1 category */}
      {filteredCategories.length > 1 && (
        <div className="rounded-lg p-2 row-2 bg-[#F4F4F5] dark:bg-BG-dark w-full h-[42px] mt-2 mb-6">
          {filteredCategories.map((item: any, index: number) => (
            <button
              key={index}
              className={`flex-1 py-[6px] px-[6px] bp:px-[10px] w-[140px] bp:w-[153px] h-[34px] text-xs place-content-center ${
                selectedCategoryId === item.id
                  ? "inter-xs-semibold-dark rounded-lg dark:text-white bg-[#FCFCFD] dark:bg-[#FFFFFF1A]"
                  : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
              }`}
              onClick={() => {
                setSelectedCategoryId(item.id);
                setSelectedPlan(null);
              }}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}

      {/* PlanLists  */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-2">
        {plans.map((plan: any, index: number) => {
          // alert(JSON.stringify(plan));

          // wallet_price = selling price, discount field = original price
          const hasDiscount =
            plan.discount > 0 && plan.discount > plan.wallet_price;
          const discountPercent = hasDiscount
            ? ((plan.discount - plan.wallet_price) / plan.discount) * 100
            : 0;
          const { symbol, value } = formatConvertedPrice(
            plan.wallet_price,
            plan.price_currency_id,
          );
          const dgCoinValue = formatDGCoinPrice(
            plan.wallet_price,
            plan.price_currency_id,
          );
          const convertedOriginal = hasDiscount
            ? formatConvertedPrice(plan.discount, plan.price_currency_id)
            : null;

          return (
            <div
              key={index}
              className={`flex items-center justify-between border p-1 dark:bg-[#FFFFFF1A] dark:border-tabs rounded-lg w-full min-h-[50px] dark:text-white ${
                selectedPlan?.id === plan.id ? "bg-gray-200" : ""
              }`}
              onClick={() => handlePlanClick(plan)}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                <img
                  src={
                    plan.image_url ||
                    plan.category.provider?.image_url ||
                    plan.provider?.image_url ||
                    ""
                  }
                  alt="img"
                  className="w-8 h-8 bp:w-12 bp:h-12 shrink-0"
                />

                <div className="min-w-0 flex-1 overflow-hidden">
                  <div className="flex items-center justify-between w-full">
                    <p className="text-xs bp:text-sm font-bold truncate overflow-hidden whitespace-nowrap min-w-0 flex-1">
                      {plan.name}
                    </p>
                    {hasDiscount && (
                      <span className="bg-[#FEF3F2] whitespace-nowrap rounded-xl shrink-0 ml-2">
                        <p className="text-[#D92D20] text-[10px] bp:text-xs font-semibold font-satoshi px-[4px] py-[2px]">
                          {Math.round(discountPercent)}% off
                        </p>
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {plan.price_type === "fixed" && (
                      <div className="bg-[#039EF2] text-white  px-2 py-1 rounded-2xl gap-1 flex items-center">
                        <div className="w-3 h-2  flex items-center">
                          <img
                            src="/png/dg.png"
                            alt="Coins"
                            className="w-[20px] h-[20px]"
                          />
                        </div>
                        <span className=" text-[10px] bp:text-xs font-inter font-semibold">
                          {dgCoinValue}
                        </span>
                      </div>
                    )}
                    {plan.price_type === "fixed" ? (
                      <>
                        <p className="text-black bp:text-xs text-[10px] dark:text-white whitespace-nowrap">
                          {symbol}
                          {value}
                        </p>
                        {convertedOriginal && (
                          <p className="text-gray-500 bp:text-xs text-[10px] line-through whitespace-nowrap">
                            {convertedOriginal.symbol}
                            {convertedOriginal.value}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-black bp:text-xs text-[10px] dark:text-white">
                          {plan.wallet_price}%
                        </p>
                        {hasDiscount && (
                          <p className="text-gray-500 bp:text-xs text-[10px] line-through">
                            {plan.discount}%
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recipient Details */}

      {/* display recipient details only when a plan has been selected */}
      {/* hide recipient details if productType is DG Coins */}
      {selectedPlan ? (
        props.productType !== "dg_coins" && (
          <div className="mt-4 border  border-border-border1 dark:border-tabs rounded p-3 dark:bg-[#FFFFFF1A]">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs bp:text-base font-satoshi font-semibold text-black dark:text-white">
                {recipientLabel}
              </p>
              {(props.productType === "game_cards" ||
                props.productType === "gift_cards" ||
                props.productType === "airtime" ||
                props.productType === "data") && (
                <button
                  type="button"
                  className="px-4 py-2 text-xs bp:text-sm font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2]"
                  onClick={() => {
                    const selfValue =
                      props.productType === "airtime" ||
                      props.productType === "data"
                        ? user?.phone_number
                        : user?.email;
                    if (selfValue) {
                      setUniqueNumber(selfValue);
                      setVerifyMessage("");
                      if (validationError.field === "unique_number") {
                        setValidationError({ field: "none", message: null });
                      }
                    }
                  }}
                >
                  Buy for self
                </button>
              )}
            </div>
            <div className="mt-8 flex items-center gap-2">
              <input
                type="text"
                name="unique_number"
                placeholder={recipientLabel}
                className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
                value={uniqueNumber}
                onChange={(e) => {
                  if (validationError.field === "unique_number") {
                    setValidationError({ field: "none", message: null });
                  }
                  setVerifyMessage("");
                  setUniqueNumber(e.target.value);
                }}
              />
              {shouldVerifyRecipient && (
                <button
                  type="button"
                  className="px-4 py-2 text-xs bp:text-sm font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2] disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={handleVerifyRecipient}
                  disabled={isVerifyingRecipient || !uniqueNumber.trim()}
                >
                  {isVerifyingRecipient ? "Verifying..." : "Verify"}
                </button>
              )}
            </div>
            {validationError.field === "unique_number" && (
              <p className="mt-1 text-xs text-red-500">
                {validationError.message}
              </p>
            )}
            {shouldVerifyRecipient && verifyMessage && (
              <p className="mt-2 text-[11px] bp:text-xs text-gray-600 dark:text-gray-300">
                {verifyMessage}
              </p>
            )}

            {/* Additional Input*/}
            {additionalInputEnabled && (
              <>
                <p className="text-xs bp:text-base font-satoshi font-semibold text-black dark:text-white">
                  {additionalInputLabel}
                </p>
                <input
                  name={additionalInputName}
                  type={additionalInputType}
                  placeholder={additionalInputPlaceholder}
                  className="w-full mt-4 p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
                  value={additionalInputValue}
                  onChange={handleAdditionalInputChange}
                />
                {validationError.field === "additional" && (
                  <p className="mt-1 text-xs text-red-500">
                    {validationError.message}
                  </p>
                )}
              </>
            )}

            <div className=" mt-2 bg-[#E6F5FE] rounded-3xl">
              <div className="flex items-center p-2 bp:p-4">
                <input type="checkbox" className="mr-2 text-xl" />
                <span className=" text-[10px] bp:text-sm text-gray-500 font-satoshi dark:bg-[#E6F5FE]">
                  {`Save my details for next time.`}
                </span>
              </div>
            </div>
          </div>
        )
      ) : (
        <div>
          {" "}
          <p className="text-xs text-center bp:text-base font-satoshi font-semibold text-black dark:text-white">
            Select a product!
          </p>
        </div>
      )}

      {/* Payment Card */}
      <FixedProductCard
        ref={payButtonRef}
        selectedPlan={selectedPlan}
        unique_number={uniqueNumber}
        type={props.productType}
        additionalInputValue={additionalInputValue}
        recipientLabel={recipientLabel}
        additionalInputLabel={additionalInputLabel}
        requiresRecipient={props.productType !== "dg_coins"}
        requiresAdditionalInput={additionalInputEnabled}
        onValidationError={setValidationError}
      />
    </div>
  );
};

export default PlanLists;
